/**
 * A screen — or a control inside one — that needs the cloud, shown where the page cannot reach it for the reason given
 * (`connectivity/routing.ts`: no cloud, an offline session, the cloud not callable from this page, the cloud down):
 * what it is, why it is not available here, and — where it helps — a link to the same place in the cloud portal. Also
 * a node's "no studios on this site yet", which is fixed in the portal too.
 *
 * Presentational: the text and the link come in; nothing goes out (the link opens the portal in a new tab).
 */
import { ExternalLinkIcon } from 'lucide-react';
import type { RouteUnavailableReason } from '../connectivity/routing.js';
import { Button } from './ui/button.js';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card.js';

export type CloudPortalNoticeProps = {
  readonly title: string;
  readonly detail: string;
  /** Why it is unavailable (for specs: `data-reason`), or null for a notice that is not about reaching the cloud. */
  readonly reason: RouteUnavailableReason | null;
  /** The same place in the cloud portal (an absolute URL), or null: no link (the portal would not help, or is unknown). */
  readonly href: string | null;
  /** The link's words (`Open Alerts in the cloud portal`). */
  readonly linkLabel: string;
  readonly testId: string;
  /** A narrower card inside a page (a degraded control) rather than a page of its own. */
  readonly inline?: boolean;
};

export function CloudPortalNotice({ title, detail, reason, href, linkLabel, testId, inline }: CloudPortalNoticeProps) {
  return (
    <Card className={inline === true ? 'w-full' : 'mx-auto max-w-2xl'} data-testid={testId} {...(reason === null ? {} : { 'data-reason': reason })}>
      <CardHeader>
        <CardTitle>{title}</CardTitle>
        <CardDescription>{detail}</CardDescription>
      </CardHeader>
      {href === null ? null : (
        <CardContent>
          <Button asChild variant="outline" size="sm">
            <a href={href} target="_blank" rel="noreferrer" data-testid="portal-link">
              <ExternalLinkIcon aria-hidden="true" />
              {linkLabel}
            </a>
          </Button>
        </CardContent>
      )}
    </Card>
  );
}
